import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { useImpression } from '@thd-olt-component-react/impression';
import { Sticky } from '@thd-olt-component-react/sticky';
import { useLocalStorage } from '@thd-olt-global/thd-storage-utils';
import { AssistantIcon } from './AssistantIcon';
import { ExpandableFab } from './ExpandableFab/ExpandableFab';
import { isOneOfPageType, getPageType } from '../utils/assistant-utils';
import { PAGE_TYPES } from '../constants/assistant-config';

const EXPAND_DELAY = 4000; // ms
const EXTENDED_EXPAND_DELAY = 8000; // ms
const CLOSE_DELAY = 7000; // ms

export const AssistantActionButton = ({ onClick, offsetMargin }) => {
  const [forceExpand, setForceExpand] = useState(false);
  const [session, setSession] = useLocalStorage('maa-session');
  const { lesser: isMobile } = useBreakpoint('lg');

  useEffect(() => {
    let openTimeout = null;
    let closeTimeout = null;

    const currentPageType = getPageType();

    if (
      isOneOfPageType([PAGE_TYPES.HOME, PAGE_TYPES.PIP, PAGE_TYPES.CONTENT, PAGE_TYPES.BROWSE, PAGE_TYPES.SEARCH])
      // Sessions before this version might now have the value available so we need to null check
      && !session?.pagesExpandedFABShownOn?.includes(currentPageType)
      && !session?.hasSessionInteractedWithFab
    ) {
      const OPEN_DELAY = currentPageType === PAGE_TYPES.PIP
      || currentPageType === PAGE_TYPES.SEARCH ? EXTENDED_EXPAND_DELAY : EXPAND_DELAY;
      openTimeout = setTimeout(() => {
        setForceExpand(true);
        setSession({
          ...session,
          pagesExpandedFABShownOn: [...(session?.pagesExpandedFABShownOn || []), currentPageType],
        });

        closeTimeout = setTimeout(() => setForceExpand(false), CLOSE_DELAY);
      }, OPEN_DELAY);
    }

    return () => {
      clearTimeout(openTimeout);
      clearTimeout(closeTimeout);
    };
  }, []);

  const { ref, clickID } = useImpression({ data: {
    id: '00DEFVALMA00', // *** required prop ***
    component: 'MagicApronAssistant', // *** required prop *** ==> component public export name
    name: 'MagicApronAssistant', // *** required prop *** ==> descriptive titles or component name
    position: '1',
    type: 'content',
    category: '', // Should be empty string per spec
  } });

  const fabContainerClasses = classNames(
    'sui-z-max sui-bottom-4 sui-right-2',
    {
      'sui-absolute': isMobile,
      'sui-fixed xl:sui-bottom-10 xl:sui-right-10': !isMobile,
    }
  );

  const stickyClassnames = classNames('sui-w-full sui-transition-[bottom]', {
    '!sui-duration-500': offsetMargin > 0
  });

  return (
    <Sticky
      className={stickyClassnames}
      position="bottom"
      stickyByDefault
      visibleByDefault
      offsetMargin={offsetMargin}
    >
      <div className={fabContainerClasses}>
        <ExpandableFab
          aria-label="Magic Apron Assistant Open Button"
          onClick={onClick}
          ref={ref}
          clickid={clickID}
          collapsible
          forceExpand={forceExpand}
          label="Magic Apron Assistant"
        >
          <AssistantIcon color="brand" width="24px" margin="2px" />
        </ExpandableFab>
      </div>
    </Sticky>
  );
};

AssistantActionButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  offsetMargin: PropTypes.number.isRequired,
};